import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import cls from 'classnames';

import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import Button from '@ott/button';
import CrossIcon from '@ott/icon-cross';

import { CLOSING_REASON } from '../constants';

import styles from './Content.scss';

export const CONTENT_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  WIDE: 'wide',
};

const mainLocator = 'ModalContent';

const buttonShape = {
  text: PropTypes.string,
  onClick: PropTypes.func,
  preventAutoClose: PropTypes.bool,
};

// Не забывайте про проптайпсы, ContentContainer игнорит те, что не указаны здесь
export const subComponentsShape = {
  header: PropTypes.node,
  title: PropTypes.node,
  body: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  headerButton: PropTypes.shape(buttonShape),
  primaryButton: PropTypes.shape(buttonShape),
  secondaryButton: PropTypes.shape(buttonShape),
  footerContentNode: PropTypes.node,
  headerContentNode: PropTypes.node,
  icon: PropTypes.node,
  pic: PropTypes.node,
  isAlignedLeft: PropTypes.bool,
  hasFixedHeaderAndFooter: PropTypes.bool,
};

export const contentOptionsShape = {
  /** Принудительно разместить кнопки вертикально (для кнопок с длинными текстами).
   * Вместо этого рекомендуется использовать короткие формулировки */
  forceVerticalButtonLayout: PropTypes.bool,

  /** (legacy) */
  transparent: PropTypes.bool,

  size: PropTypes.oneOf(Object.values(CONTENT_SIZE)),

  childrenClassNames: PropTypes.shape({
    header: PropTypes.string,
    closer: PropTypes.string,
    footer: PropTypes.string,
    title: PropTypes.string,
  }),
};

export const subComponents = Object.keys(subComponentsShape);

class Content extends PureComponent {
  static propTypes = {
    ...subComponentsShape,
    ...contentOptionsShape,
    hideClose: PropTypes.bool,
    closeModal: PropTypes.func,
    isFullScreen: PropTypes.bool,
    isBottomSheet: PropTypes.bool,
  };

  static defaultProps = {
    forceVerticalButtonLayout: false,
    footerNode: null,
    isFullScreen: false,
    isBottomSheet: false,
    transparent: false,
    closeModal: noop,
    size: CONTENT_SIZE.SMALL,
    childrenClassNames: {
      closer: '',
      footer: '',
      title: '',
    },
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.scrollableContainerRef = React.createRef();
    this.state = { hasTopShadow: false, hasBottomShadow: false };

    this.handleScroll = debounce(this.updateShadows, 50, {
      leading: true,
      trailing: true,
    });
  }

  componentDidMount() {
    this.updateShadows();
    window.addEventListener('resize', this.updateShadows);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateShadows);
  }

  updateShadows = () => {
    const { hasFixedHeaderAndFooter } = this.props;

    if (!hasFixedHeaderAndFooter) {
      return;
    }

    const scrollableContainerEl = this.scrollableContainerRef.current;

    if (!scrollableContainerEl) {
      return;
    }

    const { scrollTop, scrollHeight, offsetHeight } = scrollableContainerEl;

    const hasTopShadow = scrollTop > 0;

    const hasBottomShadow = scrollTop < scrollHeight - offsetHeight;

    this.setState({
      hasTopShadow,
      hasBottomShadow,
    });
  };

  handleCloserClick = () => {
    const { closeModal } = this.props;

    closeModal(true, CLOSING_REASON.CLOSER);
  };

  handleButtonClick = (buttonName, closingReason) => {
    const { closeModal } = this.props;

    const buttonConfig = this.props[buttonName];

    if (!buttonConfig) {
      return;
    }

    if (buttonConfig.onClick) {
      buttonConfig.onClick();
    }

    if (!buttonConfig.preventAutoClose) {
      closeModal(true, closingReason);
    }
  };

  handleHeaderButtonClick = () => {
    this.handleButtonClick('headerButton', CLOSING_REASON.HEADER_BUTTON);
  };

  handlePrimaryClick = () => {
    this.handleButtonClick('primaryButton', CLOSING_REASON.PRIMARY_BUTTON);
  };

  handleSecondaryClick = () => {
    this.handleButtonClick('secondaryButton', CLOSING_REASON.SECONDARY_BUTTON);
  };

  renderBody() {
    const { specificLocators, body, childrenClassNames } = this.props;

    const bodyNode = typeof body === 'function' ? body({ onClose: this.handleCloserClick }) : body;

    if (bodyNode === null || bodyNode === undefined || bodyNode === false) {
      return null;
    }

    return (
      <div
        ref={this.scrollableContainerRef}
        className={cls(styles.body, childrenClassNames.body)}
        onScroll={this.handleScroll}
        data-locator={cls(mainLocator, specificLocators, 'body')}
      >
        {bodyNode}
      </div>
    );
  }

  renderCloser() {
    const { hideClose, specificLocators, childrenClassNames } = this.props;

    if (hideClose) {
      return null;
    }

    return (
      <div
        className={cls(styles.closer, childrenClassNames.closer)}
        data-locator={cls(mainLocator, specificLocators, 'closer')}
        onClick={this.handleCloserClick}
      >
        <CrossIcon className={styles.closerIcon} size="large" />
      </div>
    );
  }

  renderFooter() {
    const {
      primaryButton,
      secondaryButton,
      forceVerticalButtonLayout,
      footerContentNode,
      childrenClassNames,
      specificLocators,
    } = this.props;
    const { hasBottomShadow } = this.state;

    if (!primaryButton && !secondaryButton && !footerContentNode) {
      return null;
    }

    return (
      <div
        className={cls(styles.footer, childrenClassNames.footer, {
          [styles['footer--forceVerticalButtonLayout']]: forceVerticalButtonLayout,
          [styles['footer--hasShadow']]: hasBottomShadow,
        })}
        data-locator={cls(mainLocator, specificLocators, 'footer')}
      >
        {primaryButton && (
          <div className={styles.buttonWrapper}>
            <Button
              withFullWidth={true}
              onClick={this.handlePrimaryClick}
              href={primaryButton.href}
              className={primaryButton.className}
              specificLocators={primaryButton.specificLocators}
              isDisabled={primaryButton.isDisabled}
            >
              {primaryButton.text}
            </Button>
          </div>
        )}

        {secondaryButton && (
          <div className={styles.buttonWrapper}>
            <Button
              withFullWidth={true}
              purpose={primaryButton ? 'secondary' : undefined}
              onClick={this.handleSecondaryClick}
              href={secondaryButton.href}
              className={secondaryButton.className}
              specificLocators={secondaryButton.specificLocators}
            >
              {secondaryButton.text}
            </Button>
          </div>
        )}
        {footerContentNode}
      </div>
    );
  }

  renderTitle() {
    const { specificLocators, title, childrenClassNames } = this.props;

    if (!title) {
      return null;
    }

    return (
      <div
        className={cls(styles.title, childrenClassNames.title)}
        data-locator={cls(mainLocator, specificLocators, 'title')}
      >
        {title}
      </div>
    );
  }

  renderPic() {
    const { pic } = this.props;

    if (!pic) {
      return null;
    }

    return <div className={styles.pic}>{pic}</div>;
  }

  renderHeaderButton({ isIllusion = false } = {}) {
    const { headerButton } = this.props;

    if (!headerButton) {
      return null;
    }

    const { specificLocators, text } = headerButton;

    if (isIllusion) {
      return <div className={styles.headerButtonEmpty}>{text}</div>;
    }

    return (
      <div
        className={styles.headerButton}
        onClick={this.handleHeaderButtonClick}
        data-locator={specificLocators}
      >
        {text}
      </div>
    );
  }

  renderHeader() {
    const { icon, header, headerButton, headerContentNode, childrenClassNames, specificLocators } =
      this.props;
    const { hasTopShadow } = this.state;

    return (
      <div
        className={cls(styles.header, childrenClassNames.header, {
          [styles['header--hasShadow']]: hasTopShadow,
        })}
        data-locator={cls(mainLocator, specificLocators, 'header')}
      >
        {icon && <div className={styles.icon}>{icon}</div>}
        {(headerButton || header) && (
          <div className={styles.headerContent}>
            {this.renderHeaderButton()}
            <div className={styles.headerTitle}>{header}</div>
            {this.renderHeaderButton({ isIllusion: true })}
          </div>
        )}
        {this.renderTitle()}
        {headerContentNode}
      </div>
    );
  }

  render() {
    const {
      className,
      specificLocators,
      isAlignedLeft,
      size,
      isFullScreen,
      transparent,
      hasFixedHeaderAndFooter,
      isBottomSheet,
    } = this.props;

    return (
      <div
        className={cls(
          styles.Content,
          styles[`Content--size_${size}`],
          {
            [styles['Content--isAlignedLeft']]: isAlignedLeft,
            [styles['Content--hasFixedHeaderAndFooter']]: hasFixedHeaderAndFooter,
            [styles['Content--isTransparent']]: transparent,
            [styles['Content--isFullScreen']]: isFullScreen,
            [styles['Content--isBottomSheet']]: isBottomSheet,
          },
          className
        )}
        data-allow-touch-move={true}
        data-locator={cls(mainLocator, specificLocators, 'container')}
      >
        {this.renderPic()}
        {this.renderHeader()}
        {this.renderBody()}
        {this.renderFooter()}
        {this.renderCloser()}
      </div>
    );
  }
}

export default Content;
